'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addBusinessDays,
  addDays,
  businessDays,
  dayDiff,
  formatHM,
  isoWeek,
  parseClock,
  parseDate,
  parseDuration,
  shiftMinutes,
  toISO,
  usFederalHolidays,
  ymd,
} from '@/lib/date-calcs';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Variant =
  | 'business-days'
  | 'add-days'
  | 'months-between'
  | 'weeks-between'
  | 'day-of-year'
  | 'age-difference'
  | 'work-hours'
  | 'time-card'
  | 'time-decimal'
  | 'add-time'
  | 'hours-from-now'
  | 'conception';

type Line = [string, string];

const long = (d: Date) =>
  d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
const plural = (n: number, w: string) => `${n.toLocaleString('en-US')} ${w}${n === 1 ? '' : 's'}`;
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function BrowserDateTimeCalc({
  config,
}: {
  config?: { toolSlug?: string; heading?: string; variant?: string };
}) {
  const variant = (config?.variant ?? 'business-days') as Variant;
  const [today, setToday] = useState('');
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [n, setN] = useState('10');
  const [unit, setUnit] = useState('days');
  const [usHolidays, setUsHolidays] = useState(true);
  const [extraHolidays, setExtraHolidays] = useState('');
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('17:30');
  const [brk, setBrk] = useState('30');
  const [rate, setRate] = useState('');
  const [card, setCard] = useState(() =>
    DAYS.map((_, i) =>
      i < 5 ? { in: '09:00', out: '17:00', brk: '30' } : { in: '', out: '', brk: '0' },
    ),
  );
  const [clock, setClock] = useState('7:45');
  const [dec, setDec] = useState('7.75');
  const [list, setList] = useState('1:45\n2:30\n0:50');
  const [hours, setHours] = useState('8');
  const [minutes, setMinutes] = useState('0');
  const [fromDate, setFromDate] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [basis, setBasis] = useState('due');
  const [cycle, setCycle] = useState('28');
  const [now, setNow] = useState<Date | null>(null);
  const reported = useRef(false);
  const touch = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? variant, '').catch(() => undefined);
  };

  useEffect(() => {
    const d = new Date();
    setNow(d);
    const iso = toISO(d);
    setToday(iso);
    setA(
      variant === 'age-difference'
        ? '1990-06-15'
        : variant === 'conception'
          ? toISO(addDays(d, 150))
          : iso,
    );
    setB(
      variant === 'age-difference'
        ? '1994-02-03'
        : toISO(addDays(d, variant === 'weeks-between' ? 70 : 90)),
    );
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, [variant]);

  const holidays = useMemo(() => {
    const set = new Set<string>();
    const names = new Map<string, string>();
    const da = parseDate(a);
    const db = parseDate(b);
    if (usHolidays && da) {
      const y0 = Math.min(da.getFullYear(), db?.getFullYear() ?? da.getFullYear());
      const y1 = Math.max(da.getFullYear(), db?.getFullYear() ?? da.getFullYear()) + 1;
      for (let y = y0; y <= y1; y += 1)
        for (const h of usFederalHolidays(y)) {
          set.add(toISO(h.date));
          names.set(toISO(h.date), h.name);
        }
    }
    for (const line of extraHolidays.split(/\s+/)) if (parseDate(line)) set.add(line);
    return { set, names };
  }, [a, b, usHolidays, extraHolidays]);

  const lines: Line[] = useMemo(() => {
    const da = parseDate(a);
    const db = parseDate(b);
    switch (variant) {
      case 'business-days': {
        if (!da || !db) return [['Business days', 'Pick both dates']];
        const r = businessDays(da, db, holidays.set);
        const inRange = [...holidays.set].filter((iso) => {
          const d = parseDate(iso)!;
          return d >= (da < db ? da : db) && d <= (da < db ? db : da) && d.getDay() % 6 !== 0;
        });
        return [
          ['Business days', `${r.business.toLocaleString('en-US')} (both dates counted)`],
          ['Calendar days', (Math.abs(dayDiff(da, db)) + 1).toLocaleString('en-US')],
          ['Weekend days skipped', String(r.weekend)],
          [
            'Holidays skipped',
            r.holiday
              ? `${r.holiday}: ${inRange.map((iso) => holidays.names.get(iso) ?? iso).join(', ')}`
              : '0',
          ],
        ];
      }
      case 'add-days': {
        if (!da) return [['Result', 'Pick a start date']];
        const k = Math.round(Number(n)) || 0;
        let out: Date;
        if (unit === 'business') out = addBusinessDays(da, k, holidays.set);
        else if (unit === 'weeks') out = addDays(da, k * 7);
        else if (unit === 'months') {
          out = new Date(da);
          const day = out.getDate();
          out.setDate(1);
          out.setMonth(out.getMonth() + k);
          out.setDate(Math.min(day, new Date(out.getFullYear(), out.getMonth() + 1, 0).getDate()));
        } else out = addDays(da, k);
        return [
          ['Result', long(out)],
          ['Calendar days from the start date', String(dayDiff(da, out))],
          ['ISO week', `Week ${isoWeek(out)}`],
        ];
      }
      case 'months-between':
      case 'weeks-between': {
        if (!da || !db) return [['Result', 'Pick both dates']];
        const days = Math.abs(dayDiff(da, db));
        const r = ymd(da, db);
        const totalMonths = r.years * 12 + r.months;
        return variant === 'months-between'
          ? [
              ['Months between', `${plural(totalMonths, 'month')} and ${plural(r.days, 'day')}`],
              [
                'In years',
                `${plural(r.years, 'year')}, ${plural(r.months, 'month')}, ${plural(r.days, 'day')}`,
              ],
              ['Months as a decimal', (days / 30.436875).toFixed(2)],
              ['Total days', days.toLocaleString('en-US')],
            ]
          : [
              [
                'Weeks between',
                `${plural(Math.floor(days / 7), 'week')} and ${plural(days % 7, 'day')}`,
              ],
              ['Weeks as a decimal', (days / 7).toFixed(2)],
              ['Total days', days.toLocaleString('en-US')],
            ];
      }
      case 'conception': {
        if (!da) return [['Estimated conception date', 'Pick a date']];
        // Naegele: due = LMP + 280 days; ovulation, and so conception, about 14 days before the
        // next period, which is 266 days before the due date. A longer cycle shifts both.
        const c = Math.min(45, Math.max(20, Number(cycle) || 28));
        const conception = basis === 'lmp' ? addDays(da, c - 14) : addDays(da, -266);
        const due = basis === 'due' ? da : basis === 'lmp' ? addDays(da, 280 + (c - 28)) : null;
        const lmp = basis === 'lmp' ? da : basis === 'due' ? addDays(da, -280) : null;
        const short = (d: Date) =>
          d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return [
          ['Estimated conception date', long(conception)],
          [
            'Most likely conception window',
            `${short(addDays(conception, -2))} to ${short(addDays(conception, 2))}`,
          ],
          [
            'Intercourse that could have led to it',
            `${short(addDays(conception, -5))} to ${short(addDays(conception, 1))}`,
          ],
          ...(due && basis !== 'due' ? ([['Estimated due date', long(due)]] as Line[]) : []),
          ...(lmp && basis !== 'lmp'
            ? ([['First day of last period (estimated)', long(lmp)]] as Line[])
            : []),
          ...(basis === 'birth'
            ? ([
                [
                  'Assumes',
                  'A full-term birth (40 weeks). Born early or late moves conception by the same amount.',
                ],
              ] as Line[])
            : []),
        ];
      }
      case 'day-of-year': {
        if (!da) return [['Day of the year', 'Pick a date']];
        const jan1 = new Date(da.getFullYear(), 0, 1, 12);
        const len = new Date(da.getFullYear(), 1, 29).getMonth() === 1 ? 366 : 365;
        const day = dayDiff(jan1, da) + 1;
        return [
          ['Day of the year', `Day ${day} of ${len}`],
          ['Days left in the year', String(len - day)],
          ['ISO week number', `Week ${isoWeek(da)}`],
          ['Quarter', `Q${Math.floor(da.getMonth() / 3) + 1}`],
          ['Year progress', `${((day / len) * 100).toFixed(1)}%`],
        ];
      }
      case 'age-difference': {
        if (!da || !db) return [['Age difference', 'Pick both birth dates']];
        const r = ymd(da, db);
        const older = da < db ? 'First person' : da > db ? 'Second person' : 'Neither';
        return [
          [
            'Age difference',
            `${plural(r.years, 'year')}, ${plural(r.months, 'month')}, ${plural(r.days, 'day')}`,
          ],
          ['In days', Math.abs(dayDiff(da, db)).toLocaleString('en-US')],
          ['In weeks', (Math.abs(dayDiff(da, db)) / 7).toFixed(1)],
          ['Older', older],
        ];
      }
      case 'work-hours': {
        const s = parseClock(start);
        const e = parseClock(end);
        if (s === null || e === null) return [['Hours worked', 'Enter start and end as HH:MM']];
        const m = shiftMinutes(s, e, Number(brk) || 0);
        const r = Number(rate);
        return [
          ['Hours worked', `${formatHM(m)} (${(m / 60).toFixed(2)} h)`],
          ...(e < s
            ? ([['Note', 'Overnight shift: the end time is on the next day']] as Line[])
            : []),
          ...(r > 0 ? ([['Pay', (r * (m / 60)).toFixed(2)]] as Line[]) : []),
        ];
      }
      case 'time-card': {
        let total = 0;
        const perDay: string[] = [];
        card.forEach((row, i) => {
          const s = parseClock(row.in);
          const e = parseClock(row.out);
          if (s === null || e === null) return;
          const m = shiftMinutes(s, e, Number(row.brk) || 0);
          total += m;
          perDay.push(`${DAYS[i]} ${formatHM(m)}`);
        });
        const r = Number(rate);
        const regular = Math.min(total, 40 * 60);
        const ot = Math.max(0, total - 40 * 60);
        return [
          ['Total hours', `${formatHM(total)} (${(total / 60).toFixed(2)} h)`],
          ['By day', perDay.join(', ') || 'Fill in at least one day'],
          ['Regular / overtime (over 40 h)', `${formatHM(regular)} / ${formatHM(ot)}`],
          ...(r > 0
            ? ([
                ['Pay (overtime at 1.5×)', ((regular / 60) * r + (ot / 60) * r * 1.5).toFixed(2)],
              ] as Line[])
            : []),
        ];
      }
      case 'time-decimal': {
        const fromClock = parseDuration(clock);
        const fromDec = Number(dec);
        return [
          [
            `${clock || '…'} as decimal hours`,
            fromClock === null
              ? 'Enter hours:minutes, like 7:45'
              : (fromClock / 60).toFixed(4).replace(/\.?0+$/, ''),
          ],
          [
            `${dec || '…'} hours as hours:minutes`,
            Number.isFinite(fromDec) ? formatHM(fromDec * 60) : 'Enter a decimal, like 7.75',
          ],
          ['In minutes', fromClock === null ? '' : `${Math.round(fromClock)} min`],
        ];
      }
      case 'add-time': {
        let total = 0;
        const bad: string[] = [];
        for (const raw of list.split('\n')) {
          if (!raw.trim()) continue;
          const v = parseDuration(raw);
          if (v === null) bad.push(raw.trim());
          else total += v;
        }
        return [
          ['Total', formatHM(total)],
          ['Decimal hours', (total / 60).toFixed(2)],
          [
            'In days, hours and minutes',
            `${Math.floor(Math.abs(total) / 1440)} d ${formatHM(Math.abs(total) % 1440)}`,
          ],
          ...(bad.length ? ([['Could not read', bad.join(', ')]] as Line[]) : []),
        ];
      }
      case 'hours-from-now': {
        if (!now) return [['Time', 'Loading…']];
        // Counts from now unless a start time is given (and a date, else today).
        const base =
          parseClock(fromTime) !== null ? new Date(`${fromDate || today}T${fromTime}`) : now;
        const out = new Date(
          base.getTime() + ((Number(hours) || 0) * 60 + (Number(minutes) || 0)) * 60_000,
        );
        return [
          ['Time', out.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })],
          ['Date', long(out)],
          [
            'From',
            `${base.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}, ${long(base)}`,
          ],
        ];
      }
    }
  }, [
    variant,
    a,
    b,
    n,
    unit,
    holidays,
    start,
    end,
    brk,
    rate,
    card,
    clock,
    dec,
    list,
    hours,
    minutes,
    now,
    fromDate,
    fromTime,
    today,
    basis,
    cycle,
  ]);

  const dateField = (label: string, value: string, set: (v: string) => void) => (
    <label className="tool-live-field">
      <span>{label}</span>
      <input
        onChange={(e) => {
          touch();
          set(e.target.value);
        }}
        type="date"
        value={value}
      />
    </label>
  );
  const textField = (label: string, value: string, set: (v: string) => void, type = 'text') => (
    <label className="tool-live-field">
      <span>{label}</span>
      <input
        inputMode={type === 'time' ? undefined : 'decimal'}
        onChange={(e) => {
          touch();
          set(e.target.value);
        }}
        type={type}
        value={value}
      />
    </label>
  );

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Date & time</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Date calculator'}</h2>
        <p className="tool-live-subtitle">
          Results update as you type, in your own time zone. Nothing is uploaded.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          {['business-days', 'months-between', 'weeks-between', 'age-difference'].includes(
            variant,
          ) ? (
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {dateField(variant === 'age-difference' ? 'First birth date' : 'Start date', a, setA)}
              {dateField(variant === 'age-difference' ? 'Second birth date' : 'End date', b, setB)}
            </div>
          ) : null}
          {variant === 'add-days' ? (
            <>
              {dateField('Start date', a, setA)}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {textField('Add (use − to subtract)', n, setN)}
                <label className="tool-live-field">
                  <span>Unit</span>
                  <select
                    onChange={(e) => {
                      touch();
                      setUnit(e.target.value);
                    }}
                    value={unit}
                  >
                    <option value="days">Days</option>
                    <option value="business">Business days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                  </select>
                </label>
              </div>
            </>
          ) : null}
          {variant === 'day-of-year' ? dateField('Date', a, setA) : null}
          {variant === 'conception' ? (
            <>
              <label className="tool-live-field">
                <span>I know the</span>
                <select
                  onChange={(e) => {
                    touch();
                    setBasis(e.target.value);
                  }}
                  value={basis}
                >
                  <option value="due">Due date</option>
                  <option value="lmp">First day of my last period</option>
                  <option value="birth">Baby’s birth date</option>
                </select>
              </label>
              {dateField(
                basis === 'due'
                  ? 'Due date'
                  : basis === 'lmp'
                    ? 'First day of last period'
                    : 'Birth date',
                a,
                setA,
              )}
              {basis === 'lmp' ? textField('Cycle length (days)', cycle, setCycle) : null}
            </>
          ) : null}
          {variant === 'business-days' || (variant === 'add-days' && unit === 'business') ? (
            <>
              <label
                className="tool-live-field tool-live-field-full"
                style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}
              >
                <input
                  checked={usHolidays}
                  onChange={(e) => {
                    touch();
                    setUsHolidays(e.target.checked);
                  }}
                  type="checkbox"
                />
                <span>Skip US federal holidays (observed dates)</span>
              </label>
              <label className="tool-live-field tool-live-field-full">
                <span>Other days off (YYYY-MM-DD, one per line)</span>
                <textarea
                  onChange={(e) => {
                    touch();
                    setExtraHolidays(e.target.value);
                  }}
                  rows={2}
                  value={extraHolidays}
                />
              </label>
            </>
          ) : null}
          {variant === 'work-hours' ? (
            <>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {textField('Start', start, setStart, 'time')}
                {textField('End', end, setEnd, 'time')}
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {textField('Unpaid break (min)', brk, setBrk)}
                {textField('Hourly rate (optional)', rate, setRate)}
              </div>
            </>
          ) : null}
          {variant === 'time-card' ? (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '3rem 1fr 1fr 4.5rem',
                  gap: '0.35rem',
                  alignItems: 'center',
                }}
              >
                <span />
                <small>In</small>
                <small>Out</small>
                <small>Break</small>
                {card.map((row, i) => (
                  <FragmentRow
                    day={DAYS[i]}
                    key={DAYS[i]}
                    onChange={(next) => {
                      touch();
                      setCard((prev) => prev.map((r, j) => (j === i ? next : r)));
                    }}
                    row={row}
                  />
                ))}
              </div>
              {textField('Hourly rate (optional)', rate, setRate)}
            </>
          ) : null}
          {variant === 'time-decimal' ? (
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {textField('Hours:minutes', clock, setClock)}
              {textField('Decimal hours', dec, setDec)}
            </div>
          ) : null}
          {variant === 'add-time' ? (
            <label className="tool-live-field tool-live-field-full">
              <span>Times to add, one per line (1:45, 2h 30m, 50m; start with − to subtract)</span>
              <textarea
                onChange={(e) => {
                  touch();
                  setList(e.target.value);
                }}
                rows={6}
                value={list}
              />
            </label>
          ) : null}
          {variant === 'hours-from-now' ? (
            <>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {textField('Hours', hours, setHours)}
                {textField('Minutes', minutes, setMinutes)}
              </div>
              <p className="tool-live-panel-note">
                Counting from now. To start from another moment, set a date and time:
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {dateField('From date (optional)', fromDate, setFromDate)}
                {textField('From time (optional)', fromTime, setFromTime, 'time')}
              </div>
            </>
          ) : null}
        </section>
        <section className="tool-live-preview">
          <div className="tool-html-meta-grid">
            {lines.map(([label, value]) => (
              <div className="tool-live-field" key={label}>
                <span>{label}</span>
                <input readOnly type="text" value={value} />
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function FragmentRow({
  day,
  row,
  onChange,
}: {
  day: string;
  row: { in: string; out: string; brk: string };
  onChange: (row: { in: string; out: string; brk: string }) => void;
}) {
  return (
    <>
      <span>{day}</span>
      <input
        aria-label={`${day} in`}
        onChange={(e) => onChange({ ...row, in: e.target.value })}
        type="time"
        value={row.in}
      />
      <input
        aria-label={`${day} out`}
        onChange={(e) => onChange({ ...row, out: e.target.value })}
        type="time"
        value={row.out}
      />
      <input
        aria-label={`${day} break minutes`}
        inputMode="numeric"
        onChange={(e) => onChange({ ...row, brk: e.target.value })}
        type="text"
        value={row.brk}
      />
    </>
  );
}
